import React, { useMemo } from 'react'

import Text from 'components_new/atoms/Text'
import SelectSearch, { Option } from 'components_new/molecules/SelectSearch'

import { sortAlphabeticalAsc } from 'utils/sortHelper'
import { DataRunsCustomerOption } from 'types/GlobalDataRuns'

/** Sentinel id for the "all customers" aggregate option. */
const ALL_CUSTOMERS_ID = 'ALL'

interface CustomerSelectOption extends Option {
  id: string
}

export interface DataHealthCustomerSelectProps {
  customers: DataRunsCustomerOption[]
  onSelect: (customerId: string | null) => void
  selectedCustomerId: string | null
}

/**
 * Single-select customer picker for the Datahälsa header, built on the
 * library's searchable `SelectSearch` (not a custom select - see the
 * design handoff). The aggregate "Alla N kunder" row is a real option so
 * clearing is never possible; `sort` is left off so that row can stay
 * pinned first while the customers below it are pre-sorted A-Ö.
 *
 * Deviation from the design handoff: SelectSearch's option API has no slot
 * for a live "Visar N av M kunder" footnote or a checkmark on the selected
 * row, so both are omitted rather than forking the component.
 */
const DataHealthCustomerSelect = (props: DataHealthCustomerSelectProps) => {
  const { customers, onSelect, selectedCustomerId } = props

  const options = useMemo<CustomerSelectOption[]>(() => {
    const sortedCustomers = [...customers].sort((a, b) =>
      sortAlphabeticalAsc(a.name, b.name)
    )

    const toOption = (
      customer: DataRunsCustomerOption
    ): CustomerSelectOption => ({
      id: customer.id,
      title: customer.name,
      endAdornment:
        customer.failCount > 0 ? (
          <Text sx={{ color: 'error.text' }} variant="body2" fontWeight="bold">
            {customer.failCount} fel
          </Text>
        ) : undefined
    })

    return [
      {
        id: ALL_CUSTOMERS_ID,
        title: `Alla ${customers.length} kunder`
      },
      ...sortedCustomers.map(toOption)
    ]
  }, [customers])

  const selected =
    options.find(
      (option) => option.id === (selectedCustomerId ?? ALL_CUSTOMERS_ID)
    ) ?? options[0]

  return (
    <SelectSearch
      disableClearable
      multiple={false}
      onChange={(option) => {
        onSelect(option.id === ALL_CUSTOMERS_ID ? null : option.id)
      }}
      options={options}
      selected={selected}
      sx={{ width: '100%' }}
    />
  )
}

DataHealthCustomerSelect.displayName = 'DataHealthCustomerSelect'
export default DataHealthCustomerSelect
